import { Directive, forwardRef, input, type SimpleChanges, type TemplateRef } from '@angular/core';
import {
  DsTooltipDirective,
  DsTooltipReferenceDirective,
  type DsTooltipPosition,
  type DsTooltipSize,
} from '@skyeng/libs/shared/ui-kit-v2/tooltip';

@Directive({
  selector: '[labTooltipReference]',
  providers: [
    {
      provide: DsTooltipReferenceDirective,
      useExisting: forwardRef(() => LaboratoryTooltipReferenceDirective),
    },
  ],
})
export class LaboratoryTooltipReferenceDirective extends DsTooltipReferenceDirective {}

@Directive({
  selector: '[labTooltip]',
  exportAs: 'labTooltip',
})
export class LaboratoryTooltipDirective extends DsTooltipDirective {
  readonly tooltipContent = input<string | undefined>(undefined, { alias: 'labTooltip' });
  readonly tooltipSize = input<DsTooltipSize>('m', { alias: 'labTooltipSize' });
  readonly tooltipType = input('default', { alias: 'labTooltipType' });
  readonly tooltipContentRef = input<TemplateRef<unknown> | undefined>(undefined, { alias: 'labTooltipContentRef' });
  readonly tooltipContentRefContext = input<Object | undefined>(undefined, { alias: 'labTooltipContentRefContext' });
  readonly tooltipActionRef = input<TemplateRef<unknown> | undefined>(undefined, { alias: 'labTooltipActionRef' });
  readonly tooltipPosition = input<DsTooltipPosition>('top', { alias: 'labTooltipPosition' });
  readonly tooltipMaxWidth = input<string | undefined>(undefined, { alias: 'labTooltipMaxWidth' });
  readonly tooltipDisabled = input(false, { alias: 'labTooltipDisabled' });
  readonly tooltipInteractive = input(false, { alias: 'labTooltipInteractive' });
  readonly tooltipIgnorePointerEvents = input(false, { alias: 'labTooltipIgnorePointerEvents' });
  readonly tooltipHideOnScrollAway = input<boolean | undefined>(undefined, { alias: 'labTooltipHideOnScrollAway' });
  readonly tooltipDisablePositioningFallback = input(false, { alias: 'labTooltipDisablePositioningFallback' });
  readonly tooltipCloseOnClickOutside = input(true, { alias: 'labTooltipCloseOnClickOutside' });
  readonly tooltipOffsetPx = input(8, { alias: 'labTooltipOffset' });
  readonly tooltipMobile = input(false, { alias: 'labTooltipMobile' });
  readonly tooltipMobileShowOnHold = input(false, { alias: 'labTooltipMobileShowOnHold' });

  override ngOnChanges(changes: SimpleChanges): void {
    this.syncInputs();
    super.ngOnChanges(this.toTooltipChanges(changes));
  }

  override ngOnInit(): void {
    this.syncInputs();
    super.ngOnInit();
  }

  private syncInputs(): void {
    this.content = this.tooltipContent();
    this.size = this.tooltipSize();
    this.type = this.tooltipType();
    this.contentRef = this.tooltipContentRef();
    this.contentRefContext = this.tooltipContentRefContext();
    this.actionRef = this.tooltipActionRef();
    this.position = this.tooltipPosition();
    this.maxWidth = this.tooltipMaxWidth();
    this.disabled = this.tooltipDisabled();
    this.interactive = this.tooltipInteractive();
    this.ignorePointerEvents = this.tooltipIgnorePointerEvents();
    this.hideOnScrollAway = this.tooltipHideOnScrollAway();
    this.disablePositioningFallback = this.tooltipDisablePositioningFallback();
    this.closeOnClickOutside = this.tooltipCloseOnClickOutside();
    this.tooltipOffset = this.tooltipOffsetPx();
    this.isMobile = this.tooltipMobile();
    this.showOnHold = this.tooltipMobileShowOnHold();
  }

  private toTooltipChanges(changes: SimpleChanges): SimpleChanges {
    return {
      content: changes.tooltipContent,
      size: changes.tooltipSize,
      type: changes.tooltipType,
      contentRef: changes.tooltipContentRef,
      contentRefContext: changes.tooltipContentRefContext,
      actionRef: changes.tooltipActionRef,
      position: changes.tooltipPosition,
      maxWidth: changes.tooltipMaxWidth,
      disabled: changes.tooltipDisabled,
      interactive: changes.tooltipInteractive,
      ignorePointerEvents: changes.tooltipIgnorePointerEvents,
      hideOnScrollAway: changes.tooltipHideOnScrollAway,
      disablePositioningFallback: changes.tooltipDisablePositioningFallback,
      closeOnClickOutside: changes.tooltipCloseOnClickOutside,
      tooltipOffset: changes.tooltipOffsetPx,
      isMobile: changes.tooltipMobile,
      showOnHold: changes.tooltipMobileShowOnHold,
    };
  }
}
